import { useCallback, useEffect, useState } from "react";
import AdminShell from "@/components/feature/AdminShell";
import CourseVideos from "./components/CourseVideos";
import {
  createGroup,
  deleteGroup,
  fetchGroups,
  updateGroup,
  type CommunityGroup,
} from "@/services/community";
import {
  challengeStatus,
  createChallenge,
  deleteChallenge,
  fetchChallenges,
  METRIC_LABELS,
  METRIC_UNITS,
  todayLocal,
  updateChallenge,
  type Challenge,
  type ChallengeMetric,
} from "@/services/challenges";

const ICON_OPTIONS: { value: string; label: string }[] = [
  { value: "ri-chat-3-line", label: "Conversación" },
  { value: "ri-radar-line", label: "Prospección" },
  { value: "ri-seedling-line", label: "Crecimiento" },
  { value: "ri-trophy-line", label: "Logros" },
  { value: "ri-graduation-cap-line", label: "Aprendizaje" },
  { value: "ri-lightbulb-line", label: "Ideas" },
  { value: "ri-heart-3-line", label: "Bienestar" },
  { value: "ri-rocket-2-line", label: "Metas" },
];

type GroupForm = { title: string; description: string; icon: string };
type ChallengeForm = {
  title: string;
  description: string;
  metric: ChallengeMetric;
  target: string;
  startsOn: string;
  endsOn: string;
  imageUrl: string;
};

function plusDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-CA");
}

const emptyGroup: GroupForm = { title: "", description: "", icon: ICON_OPTIONS[0].value };
const emptyChallenge = (): ChallengeForm => ({
  title: "",
  description: "",
  metric: "prospects_added",
  target: "10",
  startsOn: todayLocal(),
  endsOn: plusDays(7),
  imageUrl: "",
});

const inputCls =
  "w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400";

export default function AdminComunidad() {
  const [groups, setGroups] = useState<CommunityGroup[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [groupModal, setGroupModal] = useState<{ editing: CommunityGroup | null } | null>(null);
  const [groupForm, setGroupForm] = useState<GroupForm>(emptyGroup);
  const [challengeModal, setChallengeModal] = useState<{ editing: Challenge | null } | null>(null);
  const [challengeForm, setChallengeForm] = useState<ChallengeForm>(emptyChallenge());
  const [formError, setFormError] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2600);
  };

  const load = useCallback(async () => {
    const [g, c] = await Promise.all([fetchGroups(true), fetchChallenges()]);
    setGroups(g);
    setChallenges(c);
  }, []);

  useEffect(() => {
    let active = true;
    load()
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : "No se pudo cargar la información.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [load]);

  // ----- Grupos -----
  const openGroup = (g: CommunityGroup | null) => {
    setFormError(null);
    setGroupForm(g ? { title: g.title, description: g.description, icon: g.icon } : emptyGroup);
    setGroupModal({ editing: g });
  };

  const submitGroup = async () => {
    if (!groupModal) return;
    setSaving(true);
    setFormError(null);
    try {
      const payload = { title: groupForm.title.trim(), description: groupForm.description.trim(), icon: groupForm.icon };
      if (groupModal.editing) await updateGroup(groupModal.editing.id, payload);
      else await createGroup(payload);
      setGroupModal(null);
      await load();
      showToast(groupModal.editing ? "Grupo actualizado" : "Grupo creado");
    } catch {
      setFormError("No se pudo guardar el grupo. Revisa el nombre (2 a 60 caracteres).");
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = async (g: CommunityGroup) => {
    try {
      await updateGroup(g.id, { active: !g.active });
      await load();
    } catch {
      showToast("No se pudo cambiar el estado del grupo.");
    }
  };

  const removeGroup = async (g: CommunityGroup) => {
    if (!window.confirm(`¿Eliminar el grupo "${g.title}"? También se borrarán sus publicaciones.`)) return;
    try {
      await deleteGroup(g.id);
      await load();
      showToast("Grupo eliminado");
    } catch {
      showToast("No se pudo eliminar el grupo.");
    }
  };

  // ----- Retos -----
  const openChallenge = (c: Challenge | null) => {
    setFormError(null);
    setChallengeForm(
      c
        ? {
            title: c.title,
            description: c.description,
            metric: c.metric,
            target: String(c.target),
            startsOn: c.startsOn,
            endsOn: c.endsOn,
            imageUrl: c.imageUrl ?? "",
          }
        : emptyChallenge()
    );
    setChallengeModal({ editing: c });
  };

  const submitChallenge = async () => {
    if (!challengeModal) return;
    const target = Number(challengeForm.target);
    if (!Number.isInteger(target) || target < 1) {
      setFormError("La meta debe ser un número entero mayor a 0.");
      return;
    }
    if (challengeForm.endsOn < challengeForm.startsOn) {
      setFormError("La fecha final no puede ser anterior a la inicial.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        title: challengeForm.title.trim(),
        description: challengeForm.description.trim(),
        metric: challengeForm.metric,
        target,
        startsOn: challengeForm.startsOn,
        endsOn: challengeForm.endsOn,
        imageUrl: challengeForm.imageUrl.trim() || null,
      };
      if (challengeModal.editing) await updateChallenge(challengeModal.editing.id, payload);
      else await createChallenge(payload);
      setChallengeModal(null);
      await load();
      showToast(challengeModal.editing ? "Reto actualizado" : "Reto creado");
    } catch {
      setFormError("No se pudo guardar el reto. Revisa el título (3 a 80 caracteres).");
    } finally {
      setSaving(false);
    }
  };

  const toggleChallenge = async (c: Challenge) => {
    try {
      await updateChallenge(c.id, { active: !c.active });
      await load();
    } catch {
      showToast("No se pudo cambiar el estado del reto.");
    }
  };

  const removeChallenge = async (c: Challenge) => {
    if (!window.confirm(`¿Eliminar el reto "${c.title}"? Se pierde su ranking.`)) return;
    try {
      await deleteChallenge(c.id);
      await load();
      showToast("Reto eliminado");
    } catch {
      showToast("No se pudo eliminar el reto.");
    }
  };

  const statusLabel = { active: "En curso", upcoming: "Próximo", finished: "Finalizado" } as const;

  return (
    <AdminShell title="Comunidad y retos" description="Sube los videos de cada curso, crea los grupos por tema de la comunidad y los retos del equipo. El progreso de cada reto se calcula con la actividad real.">
      {loading ? (
        <div className="bg-background-100 border border-background-200 rounded-2xl p-10 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
        </div>
      ) : error ? (
        <div className="bg-background-100 border border-dashed border-accent-400 rounded-2xl p-10 text-center text-accent-700">{error}</div>
      ) : (
        <div className="flex flex-col gap-8">
          <CourseVideos onToast={showToast} />
          {/* Grupos */}
          <section>
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-heading font-bold text-foreground-950">Grupos por tema</h2>
              <button
                onClick={() => openGroup(null)}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line mr-1"></i>Nuevo grupo
              </button>
            </div>
            <div className="mt-3 bg-background-100 border border-background-200 rounded-2xl overflow-hidden">
              <ul className="divide-y divide-background-200">
                {groups.map((g) => (
                  <li key={g.id} className="p-4 flex items-center gap-3">
                    <span className="w-10 h-10 rounded-xl bg-primary-500/15 text-primary-700 flex items-center justify-center flex-shrink-0">
                      <i className={`${g.icon} text-lg`}></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold text-foreground-950 text-sm truncate">{g.title}</div>
                      <div className="text-xs text-foreground-500 truncate">{g.description || "Sin descripción"}</div>
                    </div>
                    <button
                      onClick={() => toggleGroup(g)}
                      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap ${g.active ? "bg-primary-500/15 text-primary-700" : "bg-background-200 text-foreground-600"}`}
                    >
                      {g.active ? "Visible" : "Oculto"}
                    </button>
                    <button onClick={() => openGroup(g)} aria-label="Editar grupo" className="w-8 h-8 rounded-full text-foreground-500 hover:bg-background-200 cursor-pointer">
                      <i className="ri-edit-line"></i>
                    </button>
                    <button onClick={() => removeGroup(g)} aria-label="Eliminar grupo" className="w-8 h-8 rounded-full text-foreground-500 hover:text-accent-600 hover:bg-background-200 cursor-pointer">
                      <i className="ri-delete-bin-6-line"></i>
                    </button>
                  </li>
                ))}
                {groups.length === 0 ? <li className="p-8 text-center text-sm text-foreground-500">Aún no hay grupos.</li> : null}
              </ul>
            </div>
          </section>

          {/* Retos */}
          <section>
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-heading font-bold text-foreground-950">Retos del equipo</h2>
              <button
                onClick={() => openChallenge(null)}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line mr-1"></i>Nuevo reto
              </button>
            </div>
            <div className="mt-3 bg-background-100 border border-background-200 rounded-2xl overflow-hidden">
              <ul className="divide-y divide-background-200">
                {challenges.map((c) => (
                  <li key={c.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-bold text-foreground-950 text-sm">{c.title}</div>
                      <div className="text-xs text-foreground-500">
                        {statusLabel[challengeStatus(c)]} · {c.startsOn} → {c.endsOn} · Meta {c.target} {METRIC_UNITS[c.metric]} ·{" "}
                        {c.participants} {c.participants === 1 ? "participante" : "participantes"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleChallenge(c)}
                        className={`text-[11px] font-semibold px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap ${c.active ? "bg-primary-500/15 text-primary-700" : "bg-background-200 text-foreground-600"}`}
                      >
                        {c.active ? "Publicado" : "Oculto"}
                      </button>
                      <button onClick={() => openChallenge(c)} aria-label="Editar reto" className="w-8 h-8 rounded-full text-foreground-500 hover:bg-background-200 cursor-pointer">
                        <i className="ri-edit-line"></i>
                      </button>
                      <button onClick={() => removeChallenge(c)} aria-label="Eliminar reto" className="w-8 h-8 rounded-full text-foreground-500 hover:text-accent-600 hover:bg-background-200 cursor-pointer">
                        <i className="ri-delete-bin-6-line"></i>
                      </button>
                    </div>
                  </li>
                ))}
                {challenges.length === 0 ? <li className="p-8 text-center text-sm text-foreground-500">Aún no hay retos. Crea el primero.</li> : null}
              </ul>
            </div>
          </section>
        </div>
      )}

      {/* Modal grupo */}
      {groupModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-background-950/70 backdrop-blur-sm" onClick={() => setGroupModal(null)}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-2xl p-5">
            <h3 className="font-heading font-bold text-foreground-950">{groupModal.editing ? "Editar grupo" : "Nuevo grupo"}</h3>
            <div className="mt-4 space-y-3">
              <input value={groupForm.title} onChange={(e) => setGroupForm((f) => ({ ...f, title: e.target.value }))} placeholder="Nombre del grupo" maxLength={60} className={inputCls} />
              <textarea value={groupForm.description} onChange={(e) => setGroupForm((f) => ({ ...f, description: e.target.value }))} placeholder="Descripción (opcional)" rows={3} maxLength={300} className={`${inputCls} resize-none`} />
              <select value={groupForm.icon} onChange={(e) => setGroupForm((f) => ({ ...f, icon: e.target.value }))} className={`${inputCls} cursor-pointer`}>
                {ICON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    Ícono: {o.label}
                  </option>
                ))}
              </select>
            </div>
            {formError ? <p className="mt-3 text-sm text-accent-700">{formError}</p> : null}
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setGroupModal(null)} className="px-4 py-2 rounded-full text-sm font-semibold text-foreground-600 hover:bg-background-100 cursor-pointer">Cancelar</button>
              <button onClick={submitGroup} disabled={saving || groupForm.title.trim().length < 2} className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-50 cursor-pointer">
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Modal reto */}
      {challengeModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-background-950/70 backdrop-blur-sm" onClick={() => setChallengeModal(null)}></div>
          <div className="relative w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto bg-background-50 rounded-2xl p-5">
            <h3 className="font-heading font-bold text-foreground-950">{challengeModal.editing ? "Editar reto" : "Nuevo reto"}</h3>
            <div className="mt-4 space-y-3">
              <input value={challengeForm.title} onChange={(e) => setChallengeForm((f) => ({ ...f, title: e.target.value }))} placeholder="Título (ej. Semana de 10 prospectos)" maxLength={80} className={inputCls} />
              <textarea value={challengeForm.description} onChange={(e) => setChallengeForm((f) => ({ ...f, description: e.target.value }))} placeholder="Descripción (opcional)" rows={3} maxLength={500} className={`${inputCls} resize-none`} />
              <label className="block text-xs font-semibold text-foreground-600">
                ¿Qué se mide?
                <select value={challengeForm.metric} onChange={(e) => setChallengeForm((f) => ({ ...f, metric: e.target.value as ChallengeMetric }))} className={`${inputCls} mt-1 cursor-pointer`}>
                  {(Object.keys(METRIC_LABELS) as ChallengeMetric[]).map((m) => (
                    <option key={m} value={m}>
                      {METRIC_LABELS[m]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs font-semibold text-foreground-600">
                Meta por persona
                <input value={challengeForm.target} onChange={(e) => setChallengeForm((f) => ({ ...f, target: e.target.value.replace(/\D/g, "") }))} inputMode="numeric" className={`${inputCls} mt-1`} />
              </label>
              <input value={challengeForm.imageUrl} onChange={(e) => setChallengeForm((f) => ({ ...f, imageUrl: e.target.value }))} placeholder="Imagen del reto (opcional, enlace https://… o /ruta)" className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs font-semibold text-foreground-600">
                  Desde
                  <input type="date" value={challengeForm.startsOn} onChange={(e) => setChallengeForm((f) => ({ ...f, startsOn: e.target.value }))} className={`${inputCls} mt-1`} />
                </label>
                <label className="block text-xs font-semibold text-foreground-600">
                  Hasta
                  <input type="date" value={challengeForm.endsOn} onChange={(e) => setChallengeForm((f) => ({ ...f, endsOn: e.target.value }))} className={`${inputCls} mt-1`} />
                </label>
              </div>
            </div>
            {formError ? <p className="mt-3 text-sm text-accent-700">{formError}</p> : null}
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setChallengeModal(null)} className="px-4 py-2 rounded-full text-sm font-semibold text-foreground-600 hover:bg-background-100 cursor-pointer">Cancelar</button>
              <button onClick={submitChallenge} disabled={saving || challengeForm.title.trim().length < 3 || !challengeForm.startsOn || !challengeForm.endsOn} className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-50 cursor-pointer">
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background-950 text-foreground-50 text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">{toast}</div>
      ) : null}
    </AdminShell>
  );
}
